/**
 * Dashboard Hero Banner Component
 * Contextual fading hero images for dashboard pages
 */

import { useState, useEffect } from "react";
import { Search, X } from "lucide-react";

// Image collections by context
const DASHBOARD_IMAGES = {
  // Admin Dashboard
  admin: {
    overview: [
      "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=1920&q=80", // Hospital exterior
      "https://images.unsplash.com/photo-1538108149393-fbbd81895907?w=1920&q=80", // Modern hospital
    ],
    hospitals: [
      "https://images.unsplash.com/photo-1586773860418-d37222d8fce3?w=1920&q=80", // Hospital corridor
      "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=1920&q=80", // Medical facility
    ],
    backorders: [
      "https://images.unsplash.com/photo-1587854692152-cbe660dbde88?w=1920&q=80", // Medical supplies
      "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=1920&q=80", // Pharmacy/supplies
    ],
    contractors: [
      "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=1920&q=80", // Technician work
      "https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=1920&q=80", // Equipment repair
    ],
    suppliers: [
      "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1920&q=80", // Warehouse
      "https://images.unsplash.com/photo-1553413077-190dd305871c?w=1920&q=80", // Logistics
    ],
    analytics: [
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1920&q=80", // Data analytics
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1920&q=80", // Dashboard screens
    ],
    compliance: [
      "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1920&q=80", // Documents
      "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1920&q=80", // Clipboard audit
    ],
  },
  
  // Hospital Dashboard
  hospital: {
    overview: [
      "https://images.unsplash.com/photo-1516549655169-df83a0774514?w=1920&q=80", // Operating room
      "https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=1920&q=80", // Medical team
    ],
    maintenance: [
      "https://images.unsplash.com/photo-1581093458791-9f3c3900df4b?w=1920&q=80", // Medical equipment
      "https://images.unsplash.com/photo-1530497610245-94d3c16cda28?w=1920&q=80", // Hospital equipment
    ],
    backorders: [
      "https://images.unsplash.com/photo-1587854692152-cbe660dbde88?w=1920&q=80", // Medical supplies
      "https://images.unsplash.com/photo-1583912267550-d6c2ac3196c0?w=1920&q=80", // Surgical tools
    ],
    quickbids: [
      "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1920&q=80", // Business deal
      "https://images.unsplash.com/photo-1434626881859-194d67b2b86f?w=1920&q=80", // Handshake
    ],
    equipment: [
      "https://images.unsplash.com/photo-1516549655169-df83a0774514?w=1920&q=80", // OR equipment
      "https://images.unsplash.com/photo-1581093588401-fbb62a02f120?w=1920&q=80", // Medical devices
    ],
    signoffs: [
      "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=1920&q=80", // Doctor with tablet
      "https://images.unsplash.com/photo-1559757175-5700dde675bc?w=1920&q=80", // Checklist
    ],
    water: [
      "https://images.unsplash.com/photo-1582719471384-894fbb16e074?w=1920&q=80", // Lab testing
      "https://images.unsplash.com/photo-1579165466991-467135ad3110?w=1920&q=80", // Water testing
    ],
    analytics: [
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1920&q=80", // Analytics
      "https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=1920&q=80", // Charts
    ],
    settings: [
      "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=1920&q=80", // Office settings
    ],
  },
  
  // Supplier Dashboard
  supplier: {
    overview: [
      "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1920&q=80", // Warehouse
      "https://images.unsplash.com/photo-1553413077-190dd305871c?w=1920&q=80", // Logistics center
    ],
    quotes: [
      "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1920&q=80", // Documents
      "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1920&q=80", // Paperwork
    ],
    orders: [
      "https://images.unsplash.com/photo-1566576912321-d58ddd7a6088?w=1920&q=80", // Shipping boxes
      "https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=1920&q=80", // Package delivery
    ],
    inventory: [
      "https://images.unsplash.com/photo-1587854692152-cbe660dbde88?w=1920&q=80", // Medical inventory
      "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=1920&q=80", // Supplies shelf
    ],
    quickbids: [
      "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1920&q=80", // Bidding
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1920&q=80", // Competition
    ],
  },
  
  // Contractor Dashboard
  contractor: {
    overview: [
      "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=1920&q=80", // Technician
      "https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=1920&q=80", // Equipment work
    ],
    jobs: [
      "https://images.unsplash.com/photo-1581093588401-fbb62a02f120?w=1920&q=80", // Medical equipment service
      "https://images.unsplash.com/photo-1530497610245-94d3c16cda28?w=1920&q=80", // Hospital tech
    ],
    schedule: [
      "https://images.unsplash.com/photo-1506784983877-45594efa4cbe?w=1920&q=80", // Calendar
      "https://images.unsplash.com/photo-1435527173128-983b87201f4d?w=1920&q=80", // Schedule planning
    ],
    certifications: [
      "https://images.unsplash.com/photo-1434626881859-194d67b2b86f?w=1920&q=80", // Certificates
      "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=1920&q=80", // Documents
    ],
    earnings: [
      "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1920&q=80", // Financial docs
      "https://images.unsplash.com/photo-1554224154-26032ffc0d07?w=1920&q=80", // Reports
    ],
  },
};

// Titles for each section
const SECTION_TITLES = {
  admin: {
    overview: "Command Center",
    hospitals: "Hospital Network",
    backorders: "Supply Chain",
    contractors: "Service Network",
    suppliers: "Supplier Network",
    analytics: "Performance Analytics",
    compliance: "Compliance Hub",
  },
  hospital: {
    overview: "Operations Dashboard",
    maintenance: "Maintenance Hub",
    backorders: "Supply Chain",
    quickbids: "Competitive Bidding",
    equipment: "Asset Management",
    signoffs: "Work Verification",
    water: "Environmental Testing",
    analytics: "Performance Insights",
    settings: "Configuration",
  },
  supplier: {
    overview: "Supplier Portal",
    quotes: "Quote Management",
    orders: "Order Fulfillment",
    inventory: "Inventory Control",
    quickbids: "Active Bids",
  },
  contractor: {
    overview: "Contractor Portal",
    jobs: "Active Jobs",
    schedule: "Work Schedule",
    certifications: "Credentials",
    earnings: "Earnings Report",
  },
};

export default function DashboardHeroBanner({ 
  portalType = "hospital", // "admin", "hospital", "supplier", "contractor"
  activeSection = "overview",
  height = "140px"
}) {
  const [currentImage, setCurrentImage] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // Get images for current portal and section
  const images = DASHBOARD_IMAGES[portalType]?.[activeSection] || 
                 DASHBOARD_IMAGES[portalType]?.overview || 
                 DASHBOARD_IMAGES.hospital.overview;
  
  const title = SECTION_TITLES[portalType]?.[activeSection] || "Dashboard";
  
  // Rotate images every 8 seconds
  useEffect(() => {
    if (images.length <= 1) return;
    
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentImage((prev) => (prev + 1) % images.length);
        setIsTransitioning(false);
      }, 500);
    }, 8000);
    
    return () => clearInterval(interval);
  }, [images.length, activeSection]);
  
  // Reset image index when section changes
  useEffect(() => {
    setCurrentImage(0);
  }, [activeSection]);
  
  return (
    <div 
      className="relative w-full overflow-hidden rounded-xl mb-4 sm:mb-6"
      style={{ height }}
    >
      {/* Background Image with Fade */}
      <div 
        className={`absolute inset-0 bg-cover bg-center transition-opacity duration-500 ${
          isTransitioning ? 'opacity-0' : 'opacity-100'
        }`}
        style={{ 
          backgroundImage: `url(${images[currentImage]})`,
        }}
      />
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-slate-900/40" />
      
      {/* Bottom fade */}
      <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-slate-50 to-transparent" />
      
      {/* Content */}
      <div className="relative h-full flex items-center justify-between px-4 sm:px-6">
        {/* Left: Title */}
        <div>
          <p className="text-xs font-medium text-blue-400 uppercase tracking-wider mb-1">
            {portalType.charAt(0).toUpperCase() + portalType.slice(1)} Portal
          </p>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white">
            {title}
          </h1>
        </div>
      </div>
      
      {/* Image indicator dots */}
      {images.length > 1 && (
        <div className="absolute bottom-3 right-4 flex gap-1.5">
          {images.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentImage(idx)}
              className={`w-2 h-2 rounded-full transition-all ${
                idx === currentImage 
                  ? 'bg-white w-4' 
                  : 'bg-white/40 hover:bg-white/60'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Separate Search Bar Component to be used below the hero banner
export function DashboardSearchBar({
  placeholder = "Search...",
  onSearch = null,
  className = ""
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (onSearch && searchQuery.trim()) {
      onSearch(searchQuery.trim());
    }
  };

  return (
    <form onSubmit={handleSearchSubmit} className={`relative ${className}`}>
      <div className={`flex items-center transition-all duration-300 w-full sm:w-auto ${
        isSearchFocused ? 'sm:w-80' : 'sm:w-72'
      }`}>
        <div className="relative w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setIsSearchFocused(true)}
            onBlur={() => setIsSearchFocused(false)}
            placeholder={placeholder}
            className="w-full pl-10 pr-10 py-2.5 bg-white border border-slate-200 rounded-lg text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm hover:border-slate-300 transition-all text-sm sm:text-base"
            data-testid="dashboard-search-input"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </form>
  );
}

export { DASHBOARD_IMAGES, SECTION_TITLES };
